<template>
    <div class="tabs" :style="`--tab-count: ${tabs.length}`">
        <button
            v-for="tab in tabs"
            :key="tab.id"
            class="tab-btn"
            :class="{ 'is-active': activeTab === tab.id }"
            @click="setActiveTab(tab.id)"
            @keydown.prevent="(event) => keyboadNavigation(event, tab)"
            :ref="(el) => setTabRef(el, tab.id)"
        >
            <NuxtIcon :name="tab.icon" class="tab-btn__icon" :class="{ 'rotate-180': tab.id === 'fasttrack' }" />
            <span class="tab-btn__text">{{ tab.name }}</span>
        </button>
        <button v-if="showExtraTabs" class="tab-btn" @click="openExtraTabs">
            <NuxtIcon name="ri:more-2-fill" class="tab-btn__icon" />
            <span class="tab-btn__text">More</span>
        </button>
    </div>

    <template v-if="showExtraTabs">
        <Backdrop v-model="extraTabsOnMobileOpen" v-show="extraTabsOnMobileOpen">
            <div class="extra-tabs">
                <div class="extra-tabs__mobile-header">
                    <h3>More Extras</h3>
                    <NuxtIcon
                        name="ri:close-fill"
                        size="32px"
                        aria-label="Close"
                        class="extra-tabs__mobile-close"
                        @click.stop.prevent="extraTabsOnMobileOpen = false"
                    />
                </div>
                <div class="extra-tabs__links">
                    <button
                        v-for="tab in extraTabs"
                        :key="tab.id"
                        class="extra-tabs__link"
                        @click="selectExtraTab(tab)"
                    >
                        {{ tab.name }}
                    </button>
                </div>
            </div>
        </Backdrop>
    </template>
</template>

<script setup lang="ts">
import { TABS } from './constants';
const { $useLDClient } = useNuxtApp();
import Backdrop from './components/Common/Backdrop.vue';

let ldClient;
onMounted(() => {
    ldClient = $useLDClient();
});

import screens from '#tailwind-config/theme/screens';
const { isMobileView } = useIsMobileView(screens.sm);

const itemRefs = useTemplateRef('items');

const { activeTabs, activeTab } = defineProps<{
    activeTabs;
    activeTab;
}>();

const activeTabsIds = activeTabs?.map((tab) => tab.tabType);
const emit = defineEmits(['setActiveTab', 'focusFirstElement']);
const setActiveTab = (tab) => {
    ldClient.track(`clicked-tab-${tab}`);
    emit('setActiveTab', tab);
};
const { t } = useI18n();

const tabsList = {
    'airport-parking': {
        name: t('searchWidget.airport-parking.tabName'),
        id: TABS.AIRPORT_PARKING,
        icon: 'ri:parking-box-line',
    },
    fasttrack: {
        name: t('searchWidget.fasttrack.tabName'),
        id: TABS.FASTTRACK,
        icon: 'ri:rewind-line',
    },
    'airport-lounges': {
        name: t('searchWidget.airport-lounges.tabName'),
        id: TABS.AIRPORT_LOUNGES,
        icon: 'ri:sofa-line',
    },
    'hotel-and-parking': {
        name: t('searchWidget.hotel-and-parking.tabName'),
        id: TABS.HOTEL_AND_PARKING,
        icon: 'ri:hotel-line',
    },
    'port-parking': {
        name: t('searchWidget.port-parking.tabName'),
        id: TABS.PORT_PARKING,
        icon: 'ri:anchor-line',
    },
    'airport-transfer': {
        name: 'Airport Transfer',
        id: 'airport-transfer',
        icon: 'ri-bus-2-line',
    },
    'travel-insurance': {
        name: t('searchWidget.travel-insurance.tabName'),
        id: TABS.TRAVEL_INSURANCE,
        icon: 'ri:earth-line',
    },
};

// Store refs for each tab button
const tabRefs = ref({});
// Function to set individual tab refs
const setTabRef = (el, tabId) => {
    if (el) {
        tabRefs.value[tabId] = el;
    }
};

const isTabSelectedByKeyboard = ref(false);

const keyboadNavigation = (event, tab) => {
    const currentTabIndex = activeTabsIds.findIndex((activeTab) => activeTab === tab.id);
    let targetIndex = currentTabIndex;

    if (event.code === 'ArrowRight') {
        targetIndex = currentTabIndex < activeTabsIds.length - 1 ? currentTabIndex + 1 : 0;
    }

    if (event.code === 'ArrowLeft') {
        targetIndex = currentTabIndex > 0 ? currentTabIndex - 1 : activeTabsIds.length - 1;
    }

    const targetTabId = activeTabsIds[targetIndex];
    tabRefs.value[targetTabId]?.focus();

    if (event.code === 'Enter') {
        setActiveTab(targetTabId);
        isTabSelectedByKeyboard.value = true;
    }

    if (event.code === 'Tab') {
        if (isTabSelectedByKeyboard.value) {
            emit('focusFirstElement');
        }
    }
};

// Extra tabs on mobile
const extraTabsOnMobileOpen = ref(false);
const activeExtraTab = ref(null);
const showExtraTabs = computed(() => isMobileView.value && extraTabs.value);
const activeTabsFormatted = activeTabsIds?.map((tabId, index) => {
    return { ...tabsList[tabId], isExtraTab: index > 3 };
});

const tabs = computed(() => {
    if (!isMobileView.value) return activeTabsFormatted;
    if (!activeExtraTab.value) return activeTabsFormatted.slice(0, 4);
    return [...activeTabsFormatted.slice(0, 3), activeExtraTab.value];
});

const extraTabs = computed(() => {
    if (!isMobileView.value || activeTabs.length < 5) return null;
    if (!activeExtraTab.value) return activeTabsFormatted.slice(4);
    const withoutActiveExtra = activeTabsFormatted.slice(4).filter((tab) => tab.id !== activeExtraTab.value.id);
    return [activeTabsFormatted[3], ...withoutActiveExtra];
});

const openExtraTabs = () => {
    extraTabsOnMobileOpen.value = true;
};

const selectExtraTab = (tab) => {
    extraTabsOnMobileOpen.value = false;
    emit('setActiveTab', tab.id);
    activeExtraTab.value = tab.isExtraTab ? tab : null;
};
</script>

<style lang="postcss" scoped>
.tabs {
    @apply flex;
    .tab-btn {
        @apply flex-grow;
        width: calc(100% / var(--tab-count));
    }
}

.tab-btn {
    @apply flex flex-col items-center p-2 gap-2 bg-white border-t border-b focus-visible:outline-white focus-visible:outline-4 -outline-offset-2;
    & + .tab-btn {
        @apply border-l border-wl-base-borders;
    }
    &:first-child {
        @apply rounded-tl-lg border-l;
    }
    &:last-child {
        @apply rounded-tr-lg border-r;
    }
    &.is-active {
        @apply text-white border-b-transparent focus-visible:outline-wl-brand-pr;
    }
    &__icon {
        @apply h-8 w-8;
    }
    &__text {
        @apply text-xs md:text-base font-medium break-words hyphens-auto;
        .is-active & {
            @apply font-bold;
        }
    }
}

.extra-tabs {
    @apply fixed w-full bottom-0 left-0 z-50 bg-white rounded-t-lg border-t border-wl-base-borders;
    &__mobile-header {
        @apply font-medium text-lg border-b border-wl-base-borders 
      flex justify-between items-center pl-6 pr-4 py-4;
    }
    &__mobile-close {
        @apply text-wl-base-dark-grey;
    }
    &__link {
        @apply block w-full py-3 px-6 text-center text-base text-wl-base-dark;
        &:not(:last-child) {
            @apply border-b border-solid border-[#F5F5FC];
        }
    }
}
</style>
